
import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from '@/hooks/useDebounce';

interface CommentLikeButtonProps {
  commentId: string;
  initialLikes?: number;
  initialLiked?: boolean;
  size?: 'sm' | 'default' | 'lg';
}

const LOCAL_STORAGE_COMMENT_LIKES_KEY = 'comment-likes';

const CommentLikeButton: React.FC<CommentLikeButtonProps> = ({
  commentId,
  initialLikes = 0,
  initialLiked = false,
  size = 'sm',
}) => {
  const { user } = useAuth();
  const [isLiked, setIsLiked] = useState<boolean>(initialLiked);
  const [likeCount, setLikeCount] = useState<number>(initialLikes);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const debouncedCommentId = useDebounce(commentId, 300);

  // Initial load of like status and count
  useEffect(() => {
    if (!debouncedCommentId) return;
    
    if (user) {
      fetchLikeStatus();
    }
    fetchLikeCount();

    // Set up subscription for real-time like updates
    const channel = supabase
      .channel('comment_likes_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'comment_likes',
          filter: `comment_id=eq.${debouncedCommentId}`,
        },
        () => {
          console.log('Realtime update detected for comment_likes');
          fetchLikeCount();
          if (user) fetchLikeStatus();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [debouncedCommentId, user]);

  const fetchLikeStatus = async () => {
    if (!user || !user.id || !debouncedCommentId) return;

    try {
      // First check localStorage for all users (including mock users)
      const storedLikes = localStorage.getItem(LOCAL_STORAGE_COMMENT_LIKES_KEY);
      if (storedLikes) {
        try {
          const parsedLikes = JSON.parse(storedLikes);
          if (parsedLikes[user.id] && Array.isArray(parsedLikes[user.id])) {
            setIsLiked(parsedLikes[user.id].includes(debouncedCommentId));
            return;
          }
        } catch (e) {
          console.error('Error parsing stored comment likes:', e);
        }
      }

      // For real users, also check Supabase as a fallback
      if (!user.id.startsWith('user-')) {
        const { data, error } = await supabase
          .from('comment_likes')
          .select('id')
          .eq('comment_id', debouncedCommentId)
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching comment like status:', error);
          return;
        }

        setIsLiked(!!data);
        
        // Update localStorage with this information
        updateLocalStorage(user.id, debouncedCommentId, !!data);
      }
    } catch (error) {
      console.error('Error fetching comment like status:', error);
    }
  };

  const updateLocalStorage = (userId: string, cId: string, liked: boolean) => {
    try {
      const storedLikes = localStorage.getItem(LOCAL_STORAGE_COMMENT_LIKES_KEY);
      let allLikes = {};
      
      if (storedLikes) {
        allLikes = JSON.parse(storedLikes);
      }
      
      if (!allLikes[userId]) {
        allLikes[userId] = [];
      }
      
      const index = allLikes[userId].indexOf(cId);
      
      if (liked && index === -1) {
        allLikes[userId].push(cId);
      } else if (!liked && index !== -1) {
        allLikes[userId].splice(index, 1);
      }
      
      localStorage.setItem(LOCAL_STORAGE_COMMENT_LIKES_KEY, JSON.stringify(allLikes));
    } catch (e) {
      console.error('Error updating localStorage for comment likes:', e);
    }
  };

  const fetchLikeCount = async () => {
    if (!debouncedCommentId) return;
    
    try {
      console.log('Fetching like count for comment:', debouncedCommentId);
      
      // Check localStorage first
      const storedLikes = localStorage.getItem(LOCAL_STORAGE_COMMENT_LIKES_KEY);
      let localCount = 0;
      
      if (storedLikes) {
        try {
          const allLikes = JSON.parse(storedLikes);
          Object.keys(allLikes).forEach(userId => {
            if (allLikes[userId]?.includes(debouncedCommentId)) {
              localCount++;
            }
          });
        } catch (e) {
          console.error('Error parsing stored comment likes for count:', e);
        }
      }
      
      // For real data, also get the count from Supabase
      const { count, error } = await supabase
        .from('comment_likes')
        .select('*', { count: 'exact', head: true })
        .eq('comment_id', debouncedCommentId);

      if (error) {
        console.error('Error fetching comment like count:', error);
        setLikeCount(localCount);
        return;
      }
      
      console.log('Comment like count from Supabase:', count);
      // Use the higher of the two numbers to ensure we don't miss any likes
      setLikeCount(Math.max(count || 0, localCount));
    } catch (error) {
      console.error('Error fetching comment like count:', error);
    }
  };

  const handleToggleLike = async () => {
    if (!user || !user.id) {
      toast.error('Debes iniciar sesión para dar like');
      return;
    }

    setIsLoading(true);
    console.log(`[COMMENT LIKE] User ${user.id} toggling like for comment ${debouncedCommentId}`);

    try {
      // Optimistically update UI
      const newLiked = !isLiked;
      setIsLiked(newLiked);
      setLikeCount(prevCount => newLiked ? prevCount + 1 : Math.max(0, prevCount - 1));
      
      // Always update localStorage for all users
      updateLocalStorage(user.id, debouncedCommentId, newLiked);
      
      // For real users, also update Supabase
      if (!user.id.startsWith('user-')) {
        if (newLiked) {
          // Add like
          console.log('Adding like for comment:', debouncedCommentId, 'by user:', user.id);
          const { error } = await supabase
            .from('comment_likes')
            .insert({
              comment_id: debouncedCommentId,
              user_id: user.id,
            });
            
          if (error) {
            console.error('Error adding comment like:', error);
            throw error;
          }
        } else {
          // Remove like
          console.log('Removing like for comment:', debouncedCommentId, 'by user:', user.id);
          const { error } = await supabase
            .from('comment_likes')
            .delete()
            .eq('comment_id', debouncedCommentId)
            .eq('user_id', user.id);
            
          if (error) {
            console.error('Error removing comment like:', error);
            throw error;
          }
        }
      }
      
      // Dispatch a comment-like-updated event
      try {
        const likeEvent = new CustomEvent('comment-like-updated', {
          detail: {
            commentId: debouncedCommentId,
            userId: user.id,
            isLiked: newLiked,
            count: likeCount
          }
        });
        window.dispatchEvent(likeEvent);
      } catch (e) {
        console.error('Error dispatching comment like event:', e);
      }
      
      // Refresh count after all operations
      await fetchLikeCount();
    } catch (error) {
      console.error('Error toggling comment like:', error);
      // Revert optimistic update on error
      setIsLiked(!isLiked);
      updateLocalStorage(user.id, debouncedCommentId, isLiked);
      await fetchLikeCount();
      toast.error('Error al actualizar el like del comentario');
    } finally {
      setIsLoading(false);
    }
  };

  const iconSize = size === 'sm' ? 12 : size === 'lg' ? 18 : 16;
  const buttonSize = size === 'sm' ? 'sm' : size === 'lg' ? 'default' : 'sm';

  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size={buttonSize}
        className={`${
          isLiked
            ? 'text-red-500 hover:text-red-600'
            : 'text-gray-400 hover:text-red-500'
        } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} p-1 h-auto rounded-full`}
        onClick={handleToggleLike}
        disabled={isLoading || !user}
      >
        <Heart className={`${isLiked ? 'fill-current' : ''}`} size={iconSize} />
      </Button>
      {likeCount > 0 && (
        <span className="text-xs font-medium">{likeCount}</span>
      )}
    </div>
  );
};

export default CommentLikeButton;
